'use client';

import { CDN_BASE_URL } from '@/lib/constants/cdn';
import { cn } from '@/lib/utils';
import { CitationMap } from '@/lib/utils/citations';
import { CITATIONS_CONFIG } from '@/lib/config/citations.config';
import { CitationPill } from '@/components/features/chat/components/ui/CitationPill';
import { CitationGroup } from '@/components/features/chat/components/ui/CitationGroup';
import { debounce } from 'lodash';
import Image from 'next/image';
import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TextGenerateEffectProps {
  words?: string;
  className?: string;
  loading?: boolean;
  adviceStatus?: string;
  shouldAnimate?: boolean;
  isNew?: boolean;
  isStreaming?: boolean;
  isLastAdvice?: boolean;
  showPlaceholder?: boolean;
  contentId?: string;
  /** Citation map for inline [N] badge rendering. When non-empty, [N] markers are replaced with CitationBadge. */
  citationMap?: CitationMap;
}

export const TextGenerateEffect = ({
  words,
  className,
  loading,
  adviceStatus,
  isLastAdvice,
  shouldAnimate = false,
  isNew = true,
  isStreaming = false,
  showPlaceholder = true,
  contentId,
  citationMap,
}: TextGenerateEffectProps) => {
  const messageContainerRef = useRef<HTMLDivElement>(null);

  // Detect error status directly from props or content
  const isErrorResponse = useMemo(() => {
    return (
      adviceStatus === 'error' ||
      (words &&
        typeof words === 'string' &&
        (words.startsWith('Sorry, your question') ||
          words.includes("isn't about Quebec housing law")))
    );
  }, [adviceStatus, words]);

  // Simplified content handling
  const contentToDisplay = useMemo(() => {
    // For error responses, always use the direct content
    if (isErrorResponse) {
      return typeof words === 'string' ? words : String(words || '');
    }

    // For normal content, use words directly - streaming API already provides proper markdown
    if (words && typeof words === 'string') {
      return words;
    }

    // Fallback for empty content
    return showPlaceholder ? 'No response content available.' : '';
  }, [isErrorResponse, words, showPlaceholder]);

  // Simple auto-scroll to bottom
  const scrollToBottom = useMemo(
    () =>
      debounce(() => {
        if (messageContainerRef.current) {
          const isNearBottom =
            window.innerHeight + window.scrollY >
            document.body.offsetHeight - 200;

          if (isNearBottom || isStreaming) {
            messageContainerRef.current.scrollIntoView({
              behavior: 'smooth',
              block: 'end',
            });
          }
        }
      }, 100),
    [isStreaming]
  );

  // Scroll down when content changes
  useEffect(() => {
    if (contentToDisplay && contentToDisplay.length > 20) {
      scrollToBottom();
    }
  }, [contentToDisplay, scrollToBottom]);

  // ---------------------------------------------------------------------------
  // Citation-aware text renderer
  // ---------------------------------------------------------------------------

  /**
   * Returns true when we have a non-empty citation map — we only intercept
   * [N] markers in this case so plain markdown renders at full speed otherwise.
   */
  const hasCitations = useMemo(
    () => citationMap && Object.keys(citationMap).length > 0,
    [citationMap]
  );

  /**
   * Splits a plain-text string on [N] markers and renders CitationPill
   * components. Adjacent citations (e.g. [1][2][3] with no text between)
   * are grouped into a single CitationGroup with navigation arrows.
   */
  const renderTextWithCitations = useCallback(
    (text: string): React.ReactNode => {
      if (!hasCitations || !citationMap) return text;

      // Use a fresh regex instance (stateful with /g)
      const regex = new RegExp(CITATIONS_CONFIG.CITATION_REGEX.source, 'g');

      // First pass: collect all matches with positions
      const matches: Array<{ num: number; start: number; end: number; raw: string }> = [];
      let match: RegExpExecArray | null;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          num: parseInt(match[1], 10),
          start: match.index,
          end: regex.lastIndex,
          raw: match[0],
        });
      }

      if (matches.length === 0) return text;

      // Second pass: group adjacent citations (no text between them)
      const groups: Array<{ nums: number[]; raws: string[]; start: number; end: number }> = [];
      let currentGroup = {
        nums: [matches[0].num],
        raws: [matches[0].raw],
        start: matches[0].start,
        end: matches[0].end,
      };

      for (let i = 1; i < matches.length; i++) {
        if (matches[i].start === currentGroup.end) {
          // Adjacent — extend group
          currentGroup.nums.push(matches[i].num);
          currentGroup.raws.push(matches[i].raw);
          currentGroup.end = matches[i].end;
        } else {
          // Gap — start a new group
          groups.push(currentGroup);
          currentGroup = {
            nums: [matches[i].num],
            raws: [matches[i].raw],
            start: matches[i].start,
            end: matches[i].end,
          };
        }
      }
      groups.push(currentGroup);

      // Third pass: build React nodes interleaving text and citation components
      const parts: React.ReactNode[] = [];
      let lastIndex = 0;
      let keyIdx = 0;

      for (const group of groups) {
        // Push preceding plain text
        if (group.start > lastIndex) {
          parts.push(text.slice(lastIndex, group.start));
        }

        // Filter to valid citation numbers (exist in citationMap)
        const validNums = group.nums.filter((n) => citationMap[n]);
        const invalidRaws = group.nums
          .map((n, i) => (!citationMap[n] ? group.raws[i] : null))
          .filter(Boolean);

        // Render invalid citations as plain text
        if (invalidRaws.length > 0 && validNums.length === 0) {
          parts.push(group.raws.join(''));
        } else {
          // Render any leading invalid raws as plain text
          if (invalidRaws.length > 0) {
            parts.push(invalidRaws.join(''));
          }

          if (validNums.length === 1) {
            // Single citation → render a pill
            parts.push(
              <CitationPill
                key={`cp-${validNums[0]}-${keyIdx++}`}
                citationNumber={validNums[0]}
                citationMap={citationMap}
              />
            );
          } else if (validNums.length > 1) {
            // Multiple adjacent → render a group with navigation
            parts.push(
              <CitationGroup
                key={`cg-${validNums.join('-')}-${keyIdx++}`}
                citationNumbers={validNums}
                citationMap={citationMap}
              />
            );
          }
        }

        lastIndex = group.end;
      }

      // Trailing text
      if (lastIndex < text.length) {
        parts.push(text.slice(lastIndex));
      }

      return parts.length > 0 ? <>{parts}</> : text;
    },
    [hasCitations, citationMap]
  );

  /**
   * Custom ReactMarkdown components that intercept text nodes to inject
   * citation badges. Only active when citationMap is populated.
   */
  const markdownComponents: Components | undefined = useMemo(() => {
    if (!hasCitations) return undefined;

    return {
      // Override the default text renderer for paragraphs, list items, etc.
      // ReactMarkdown passes raw text content through the `children` prop of
      // the `p`, `li`, `strong`, `em` etc. wrapper elements. We intercept at
      // the paragraph / list-item level and recursively process children.
      p: ({ children, ...props }) => (
        <p {...props}>{processChildren(children)}</p>
      ),
      li: ({ children, ...props }) => (
        <li {...props}>{processChildren(children)}</li>
      ),
      strong: ({ children, ...props }) => (
        <strong {...props}>{processChildren(children)}</strong>
      ),
      em: ({ children, ...props }) => (
        <em {...props}>{processChildren(children)}</em>
      ),
    };

    function processChildren(children: React.ReactNode): React.ReactNode {
      return React.Children.map(children, (child) => {
        if (typeof child === 'string') {
          return renderTextWithCitations(child);
        }
        return child;
      });
    }
  }, [hasCitations, renderTextWithCitations]);

  // Render the markdown content
  const renderedContent = useMemo(
    () => (
      <div className='flex w-full items-start space-x-2 sm:space-x-3 md:space-x-4'>
        <div className='hidden h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg sm:flex sm:h-12 sm:w-12 md:h-14 md:w-14'>
          <div className='relative h-[30px] w-[30px] sm:h-[40px] sm:w-[40px] md:h-[60px] md:w-[60px]'>
            <Image
              src={`${CDN_BASE_URL}/JAQLNLogo.png`}
              alt='Jaqln avatar'
              fill
              priority
              sizes='(max-width: 640px) 30px, (max-width: 768px) 40px, 60px'
              className='object-contain'
            />
          </div>
        </div>
        <div className='prose prose-sm mt-1 max-w-none flex-1 text-wrap pb-3 pr-2 text-gray-800 dark:prose-invert dark:text-gray-100 sm:mt-2 sm:pr-4 md:pr-8'>
          <div className='relative w-full'>
            <div className='prose prose-neutral w-full max-w-prose dark:prose-invert'>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {contentToDisplay}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      </div>
    ),
    [contentToDisplay, markdownComponents]
  );

  return (
    <div className={cn('flex flex-col', className)} ref={messageContainerRef}>
      {renderedContent}
    </div>
  );
};
